/**
 * The publish gate.
 *
 * Every rule that decides whether a guide is allowed to become public lives
 * here, and only here. The CMS calls it to show warnings while editing; the
 * `onPublish` function calls it again to actually block. The CMS copy is a
 * courtesy, the function call is the enforcement, and because they run the same
 * code they cannot disagree.
 */
/**
 * Failures a migration exemption can never suppress.
 *
 * The dividing line is whether the problem breaks the website or merely leaves
 * content incomplete. A duplicate slug collides a URL, a malformed slug
 * produces an unreachable page, a missing canonical creates duplicate content,
 * and a broken link is broken for readers today. None of those are made
 * acceptable by the content having shipped before the CMS existed.
 *
 * Everything else, missing sources, missing authorship, an absent direct
 * answer, is editorial debt on pages that are already public. Those are
 * recorded and scheduled rather than used to block.
 */
const NON_WAIVABLE = new Set([
    'title.missing',
    'title.duplicate',
    'slug.missing',
    'slug.format',
    'slug.duplicate',
    'canonicalUrl.missing',
    'link.broken',
    'claim.orphaned',
    'publishedAt.invalid',
]);
/** True when this exemption explicitly covers this issue. */
function waives(exemption, issue) {
    if (!exemption)
        return false;
    if (NON_WAIVABLE.has(issue.code))
        return false;
    return exemption.waivedCodes.includes(issue.code);
}
const DIRECT_ANSWER_MIN_WORDS = 40;
const DIRECT_ANSWER_MAX_WORDS = 70;
/** Claims at or above this level cannot ship unverified. */
const BLOCKING_RISK = ['compliance'];
/**
 * Subjects where an unsourced sentence is a liability rather than an opinion.
 * Zennopay is a registered MSB; a wrong statement about a regulator or a market
 * being live is not an editorial slip.
 */
const FACTUAL_SUBJECTS = [
    'regulation', 'regulator', 'licence', 'license', 'compliance', 'aml', 'kyc',
    'sanction', 'fee', 'pricing', 'cost', 'spread', 'fx', 'exchange rate',
    'settlement', 'available in', 'live in', 'supported', 'rail', 'promptpay',
    'vietqr', 'qr ph', 'paynow', 'duitnow', 'qris', 'upi', 'bakong', 'khqr',
];
const words = (text) => text.trim().split(/\s+/).filter(Boolean).length;
const error = (code, field, message) => ({ code, field, message, severity: 'error' });
const warn = (code, field, message) => ({ code, field, message, severity: 'warning' });
/** Internal hrefs in the body, normalized with a trailing slash. */
export function internalLinks(bodyHtml) {
    const found = new Set();
    const pattern = /href\s*=\s*["'](\/[^"'#?]*)/gi;
    let match;
    while ((match = pattern.exec(bodyHtml)) !== null) {
        const path = match[1];
        if (!path)
            continue;
        found.add(path.endsWith('/') || path.includes('.') ? path : `${path}/`);
    }
    return [...found];
}
/** Matches a footnote reference or an inline <cite> element. */
const CITATION = /\[\^[^\]]+\]|<cite[^>]*>[\s\S]*?<\/cite>/i;
/**
 * Body sentences that read as factual claims but carry no citation marker.
 *
 * Sentences are split before citations are examined, not after. Stripping the
 * markers up front would erase the very evidence that a sentence is sourced,
 * and every cited claim would then be reported as unsourced.
 */
export function unsourcedFactualSentences(body) {
    return body
        .split(/(?<=[.!?])\s+/)
        .filter((sentence) => !CITATION.test(sentence))
        .map((sentence) => sentence.replace(/<[^>]+>/g, '').trim())
        .filter((sentence) => {
        if (sentence.length < 25)
            return false;
        const lower = sentence.toLowerCase();
        return FACTUAL_SUBJECTS.some((subject) => lower.includes(subject));
    });
}
export function evaluatePublishGate(context) {
    const { guide, author, reviewer, claims, sources, existingSlugs, existingTitles, knownRoutes } = context;
    const issues = [];
    /* ---- identity ---- */
    if (!guide.title?.trim()) {
        issues.push(error('title.missing', 'title', 'A title is required.'));
    }
    else if (existingTitles.has(guide.title.trim().toLowerCase())) {
        issues.push(error('title.duplicate', 'title', `Another guide already uses the title "${guide.title}". Titles must be unique so metadata does not collide.`));
    }
    if (!guide.slug?.trim()) {
        issues.push(error('slug.missing', 'slug', 'A slug is required.'));
    }
    else if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(guide.slug)) {
        issues.push(error('slug.format', 'slug', 'Slugs must be lowercase words separated by single hyphens.'));
    }
    else if (existingSlugs.has(guide.slug)) {
        issues.push(error('slug.duplicate', 'slug', `The slug "${guide.slug}" is already published. Changing a live slug needs a 301 redirect.`));
    }
    /* ---- the direct answer ---- */
    if (!guide.primaryQuestion?.trim()) {
        issues.push(error('primaryQuestion.missing', 'primaryQuestion', 'Every guide must state the question it answers.'));
    }
    const answerWords = words(guide.directAnswer ?? '');
    if (!guide.directAnswer?.trim()) {
        issues.push(error('directAnswer.missing', 'directAnswer', 'A direct answer is required. It is rendered under the H1 for readers.'));
    }
    else if (answerWords < DIRECT_ANSWER_MIN_WORDS || answerWords > DIRECT_ANSWER_MAX_WORDS) {
        issues.push(error('directAnswer.length', 'directAnswer', `The direct answer is ${answerWords} words. It must be between ${DIRECT_ANSWER_MIN_WORDS} and ${DIRECT_ANSWER_MAX_WORDS} so it stands alone as an answer.`));
    }
    /* ---- attribution ---- */
    if (!guide.authorId || !author) {
        issues.push(error('author.missing', 'authorId', 'An author is required. Unattributed guides are not published.'));
    }
    if (!guide.publishedAt) {
        issues.push(error('publishedAt.missing', 'publishedAt', 'A publish date is required.'));
    }
    else if (Number.isNaN(Date.parse(guide.publishedAt))) {
        issues.push(error('publishedAt.invalid', 'publishedAt', 'The publish date is not a valid ISO-8601 date.'));
    }
    /* ---- search metadata ---- */
    if (!guide.seoTitle?.trim()) {
        issues.push(error('seoTitle.missing', 'seoTitle', 'An SEO title is required.'));
    }
    else if (guide.seoTitle.length > 60) {
        issues.push(warn('seoTitle.long', 'seoTitle', `${guide.seoTitle.length} characters. Titles past about 60 get truncated in results.`));
    }
    if (!guide.metaDescription?.trim()) {
        issues.push(error('metaDescription.missing', 'metaDescription', 'A meta description is required.'));
    }
    else if (guide.metaDescription.length > 160) {
        issues.push(warn('metaDescription.long', 'metaDescription', `${guide.metaDescription.length} characters. Descriptions past about 160 get truncated.`));
    }
    if (!guide.canonicalUrl?.trim()) {
        issues.push(error('canonicalUrl.missing', 'canonicalUrl', 'A canonical URL is required.'));
    }
    /* ---- media ---- */
    if (guide.heroImage && !guide.heroImage.alt?.trim()) {
        issues.push(error('heroImage.alt', 'heroImage', 'The hero image needs alt text. Decorative-only heroes should be removed instead.'));
    }
    if (guide.openGraphImage && !guide.openGraphImage.alt?.trim()) {
        issues.push(warn('ogImage.alt', 'openGraphImage', 'The social image has no alt text.'));
    }
    /* ---- claims and sources ---- */
    const sourceIds = new Set(sources.map((s) => s.id));
    const guideClaims = claims.filter((c) => guide.claimIds.includes(c.id));
    for (const claim of guideClaims) {
        if (!claim.sourceId || !sourceIds.has(claim.sourceId)) {
            issues.push(error('claim.orphaned', 'claimIds', `The claim "${claim.claimText.slice(0, 60)}…" points at a source that is not attached to this guide.`));
        }
        if (!claim.verifiedAt || !claim.reviewedBy) {
            const unresolved = BLOCKING_RISK.includes(claim.riskLevel);
            issues.push({
                code: 'claim.unverified',
                field: 'claimIds',
                message: `The ${claim.riskLevel}-risk claim "${claim.claimText.slice(0, 60)}…" has not been verified by a named reviewer.`,
                severity: unresolved ? 'error' : 'warning',
            });
        }
    }
    /* The condition is `guideClaims.length === 0`, so what satisfies this rule is a
       *claim* in `guide.claimIds`, not a *source* in `guide.sourceIds`. The message
       used to say the guide "cites no sources", which sent editors to attach a
       source, watch nothing change, and lose a publish cycle to it. The field is
       still reported as `sourceIds` because that is where the CMS anchors the
       citation UI, which makes the wording carry the whole burden of saying what
       the editor actually has to do. */
    const factual = unsourcedFactualSentences(guide.body ?? '');
    const firstFactual = factual[0];
    if (firstFactual && guideClaims.length === 0) {
        issues.push(error('sources.required', 'sourceIds', `This guide makes ${factual.length} factual statement(s) about payments, compliance, FX, or rails ` +
            `but has no claims attached. Attaching a source on its own does not satisfy this: add at least one ` +
            `claim to this guide (each claim is the sentence plus the source that backs it), and attach that ` +
            `claim's source to the guide too. First unsourced statement: "${firstFactual.slice(0, 90)}…"`));
    }
    /* ---- compliance review ---- */
    const isComplianceContent = guideClaims.some((c) => c.riskLevel === 'compliance') ||
        guide.categoryId === 'compliance' ||
        guide.resourceType === 'policy';
    if (isComplianceContent && !reviewer) {
        issues.push(error('reviewer.required', 'reviewerId', 'Compliance content requires a named reviewer before publishing.'));
    }
    /* ---- internal links ---- */
    for (const link of internalLinks(guide.body ?? '')) {
        if (!knownRoutes.has(link)) {
            issues.push(error('link.broken', 'body', `The body links to "${link}", which the site does not serve.`));
        }
    }
    /* ---- freshness ---- */
    if (guide.nextReviewAt && Date.parse(guide.nextReviewAt) < Date.now()) {
        issues.push(warn('review.overdue', 'nextReviewAt', 'The scheduled review date has already passed. Confirm the content is still accurate.'));
    }
    /* ---- migration exemption ---- */
    /* Waived errors are downgraded, not deleted: they stay visible in the CMS
       and keep the guide in the review queue until someone sources them. An
       error whose code the waiver does not name still blocks, so a problem
       introduced by a later edit cannot inherit the migration's leniency. */
    const waived = [];
    const remaining = [];
    for (const issue of issues) {
        if (issue.severity === 'error' && waives(guide.gateExemption, issue)) {
            waived.push({ ...issue, severity: 'warning' });
        }
        else {
            remaining.push(issue);
        }
    }
    return {
        canPublish: !remaining.some((issue) => issue.severity === 'error'),
        issues: remaining,
        waived,
    };
}
